import React, { useEffect, useContext, cloneElement, ReactNode } from 'react'
import { Controller, Message, UseControllerOptions } from 'react-hook-form'
import styled from 'styled-components'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { Moment } from 'moment'
import Box, { TBox } from '@components/Box'
import { camelCaseToDash } from '@utils/string'

import FormItem, { IFormItem, TTooltip } from '@components/FormItem'

import ultraRemConvertor from '@utils/styles/ultraRemConvertor'
import CFormContext from './CFormContext'

const Wrapper = styled(Box)`
  & .ant-form-item-explain-error,
  & .ant-form-item-explain-success {
    font-size: 12px;
    line-height: 14px;
  }
  label {
    padding-bottom: 3px;
  }
`

const defaultValuesMap: { [key: string]: any } = {
  Checkbox: false,
  DatePicker: '',
  DatePickerInput: '',
  DateRangePicker: '',
  FileUpload: '',
  Gender: '',
  Input: '',
  MaskedInput: '',
  Poll: '',
  RadioGroup: '',
  Select: '',
  Switch: false,
  Textarea: '',
}

const haveCustomHelpMap: { [key: string]: boolean } = {
  FileUpload: true,
}

const haveCustomLabelMap: { [key: string]: boolean } = {
  Checkbox: true,
  FileUpload: true,
}

export interface ICFormItemProps extends Omit<IFormItem, 'rules'>, TBox {
  name: string
  defaultValue?: string | boolean | number | Array<Moment>
  label?: string | ReactNode
  placeholder?: string
  help?: string
  disabled?: boolean
  rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>
  useControllerOptions?: UseControllerOptions
  children: JSX.Element
  error?: Message
  tooltip?: TTooltip
}

const CFormItem: React.FC<ICFormItemProps> = ({
  name,
  confirmed,
  secondLabel,
  label,
  help,
  children,
  useControllerOptions,
  defaultValue,
  placeholder = '',
  disabled,
  rules: elementRules,
  error,
  tooltip,
  mb = 20,
  ml,
  mr,
  mt,
  mx,
  my,
  pl,
  pt,
  pr,
  pb,
  px,
  py,
  ...boxProps
}) => {
  const { status, mess, rules, control, setError, formId } = useContext(CFormContext)
  const initialDefaultValue = defaultValuesMap?.[children.type.displayName] ?? undefined
  const haveCustomHelp = haveCustomHelpMap?.[children.type.displayName] ?? false
  const haveCustomLabel = haveCustomLabelMap?.[children.type.displayName] ?? false

  const itemId = `${formId}-${name}`

  const validateStatus = status(name)
  const validateHelp = mess(name) ?? help
  const validateRules = elementRules ?? rules[name] ?? {}

  const controlDefaultValue = defaultValue ?? initialDefaultValue

  useEffect(() => {
    error &&
      setError(name, {
        type: 'manual',
        message: error,
      })
  }, [error, name, setError])

  // @ts-ignore
  // @ts-ignore
  return (
    <Wrapper
      {...{ mb: ultraRemConvertor(mb) }}
      {...{ ml: ultraRemConvertor(ml) }}
      {...{ mr: ultraRemConvertor(mr) }}
      {...{ mt: ultraRemConvertor(mt) }}
      {...{ mt: ultraRemConvertor(mt) }}
      {...{ mx: ultraRemConvertor(mx) }}
      {...{ my: ultraRemConvertor(my) }}
      {...{ pb: ultraRemConvertor(pb) }}
      {...{ pl: ultraRemConvertor(pl) }}
      {...{ pr: ultraRemConvertor(pr) }}
      {...{ pt: ultraRemConvertor(pt) }}
      {...{ px: ultraRemConvertor(px) }}
      {...{ py: ultraRemConvertor(py) }}
      {...boxProps}
    >
      <FormItem
        label={!haveCustomLabel && label}
        validateStatus={validateStatus}
        help={!haveCustomHelp && validateHelp}
        confirmed={confirmed}
        secondLabel={secondLabel}
        tooltip={tooltip}
        itemId={itemId}
        /* @ts-ignore */
        className={children?.type?.displayName ? camelCaseToDash(children.type.displayName) : ''}
      >
        <Controller
          control={control}
          name={name}
          disabled={disabled}
          defaultValue={controlDefaultValue}
          placeholder={placeholder}
          render={({ onChange, onBlur, value, name }) =>
            cloneElement(children, {
              id: itemId,
              status: validateStatus,
              help: validateHelp,
              onChange: children?.props?.onChange
                ? (...args: any) => {
                    onChange(...args)
                    children?.props?.onChange(...args)
                  }
                : onChange,
              onBlur,
              value,
              name,
              disabled,
              defaultValue,
              ...(label ? { label } : {}),
            })
          }
          rules={validateRules as Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>}
          {...(useControllerOptions ?? {})}
        />
      </FormItem>
    </Wrapper>
  )
}

export default CFormItem
